import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RolePermissionsTab } from './role-permissions-tab';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useProject } from '@/hooks/use-projects';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectCombobox, type ProjectOption } from './project-combobox';
import { ProjectMembersPicker } from './project-members-picker';
import { useBoardMembers, useAddBoardMembers, useRemoveBoardMember, useChangeBoardMemberRole, useBoard, useDeleteBoard, useUpdateBoard, boardKeys, useBoardPermission, useBoardSettingsTabs, useGrantableBoardRoles, useMayChangeBoardMember } from '@/hooks/use-boards';
import {
  useBoardSettings,
  useCreateStatus,
  useCreateTag,
  useDeleteStatus,
  useDeleteTag,
  useProjectTags,
  useProjectTaskStats,
  useReorderStatuses,
  useStatuses,
  useUpdateBoardSettings,
  useUpdateStatus,
  useUpdateTag,
} from '@/hooks/use-tasks';
import { cn } from '@/lib/utils';
import type { TaskStatusItem, TaskTagItem } from '@hygge/shared';
import { Columns3, FolderKanban, GripVertical, Pencil, Plus, Shield, Sliders, Tag, Trash2, Users, X, Zap, ShieldCheck } from 'lucide-react';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { AutomationsTab } from './automations/automations-tab';
import { useEmployeeNameFormatter } from '@/lib/employee-name';

interface BoardSettingsDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBoardDeleted?: () => void;
}

const STATUS_COLORS = [
  '#6B7280',
  '#3B82F6',
  '#F59E0B',
  '#8B5CF6',
  '#10B981',
  '#EF4444',
  '#F97316',
  '#06B6D4',
  '#EC4899',
  '#6366F1',
];

const CATEGORIES = [
  { value: 'backlog', label: 'Backlog', color: '#6B7280' },
  { value: 'active', label: 'Active', color: '#3B82F6' },
  { value: 'done', label: 'Done', color: '#10B981' },
  { value: 'cancelled', label: 'Cancelled', color: '#EF4444' },
] as const;

export function BoardSettingsDialog({
  projectId,
  open,
  onOpenChange,
  onBoardDeleted,
}: BoardSettingsDialogProps) {
  const { data: board } = useBoard(projectId);
  const { data: project } = useProject(board?.projectId ?? '');
  const { data: statuses = [] } = useStatuses(projectId);
  const { data: tags = [] } = useProjectTags(projectId);
  const { data: members = [] } = useBoardMembers(projectId);
  const membersCount = members?.length ?? 0;
  // The backend asks this board for `automation.manage` on every automation write (+ board
  // admin) — don't render a tab whose every control would 403.
  // The rank, not a permission: editing the table is the one thing the table
  // itself must not be able to hand out.
  const tabs = useBoardSettingsTabs(projectId);
  // Undefined until the reader picks one, so the tab follows `tabs.first` as it
  // resolves rather than freezing on whatever was known at mount.
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  const isOwner = tabs.rolePermissions;
  const canManageAutomations = tabs.automations;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[min(800px,calc(100vw-2rem))] w-[800px] p-0 gap-0 overflow-hidden max-h-[85vh] flex flex-col rounded-xl [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Board Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Configure board columns, tags, members and general settings
        </DialogDescription>

        {/* Header — match design: gear icon, title, project badge, close */}
        <div className="flex items-center justify-between px-6 py-[18px] border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2.5">
              <svg
                className="h-[18px] w-[18px] shrink-0 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-base font-bold tracking-tight">Board Settings</span>
            </div>
            {project && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                <span
                  className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: project.color }}
                />
                {project.name}
              </span>
            )}
          </div>
          <button
            type="button"
            className="h-[30px] w-[30px] rounded-full border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors cursor-pointer"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
          >
            <X className="h-[15px] w-[15px]" />
          </button>
        </div>

        <Tabs
          value={activeTab ?? tabs.first}
          onValueChange={setActiveTab}
          className="flex flex-col flex-1 min-h-0"
        >
          {/* An owner sees six tabs. The dialog is sized for them, and on a
              window narrower than that the row scrolls instead of clipping the
              last tab, which is the one only an owner has. */}
          <TabsList
            data-slot="board-settings-tabs"
            className="w-full justify-start rounded-none border-b border-border bg-muted/40 px-6 h-auto p-0 gap-0 shrink-0 overflow-x-auto overflow-y-hidden [&>button]:flex-none [&>button]:bg-transparent [&>button[data-state=active]]:bg-transparent [&>button[data-state=active]]:shadow-none"
          >
            {tabs.columns && (
            <TabsTrigger
              value="columns"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:text-foreground data-[state=active]:font-semibold px-4 py-3 text-[13px] font-medium text-muted-foreground gap-1.5 hover:text-foreground transition-colors duration-150"
            >
              <Columns3 className="h-3.5 w-3.5" />
              Columns
              <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground tabular-nums">
                {statuses.length}
              </span>
            </TabsTrigger>
            )}
            {tabs.tags && (
            <TabsTrigger
              value="tags"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:text-foreground data-[state=active]:font-semibold px-4 py-3 text-[13px] font-medium text-muted-foreground gap-1.5 hover:text-foreground transition-colors duration-150"
            >
              <Tag className="h-3.5 w-3.5" />
              Tags
              <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground tabular-nums">
                {tags.length}
              </span>
            </TabsTrigger>
            )}
            {tabs.members && (
            <TabsTrigger
              value="members"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:text-foreground data-[state=active]:font-semibold px-4 py-3 text-[13px] font-medium text-muted-foreground gap-1.5 hover:text-foreground transition-colors duration-150"
            >
              <Users className="h-3.5 w-3.5" />
              Members
              <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground tabular-nums">
                {membersCount}
              </span>
            </TabsTrigger>
            )}
            {tabs.general && (
            <TabsTrigger
              value="general"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:text-foreground data-[state=active]:font-semibold px-4 py-3 text-[13px] font-medium text-muted-foreground gap-1.5 hover:text-foreground transition-colors duration-150"
            >
              <Sliders className="h-3.5 w-3.5" />
              General
            </TabsTrigger>
            )}
            {canManageAutomations && (
              <TabsTrigger
                value="automations"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:text-foreground data-[state=active]:font-semibold px-4 py-3 text-[13px] font-medium text-muted-foreground gap-1.5 hover:text-foreground transition-colors duration-150"
              >
                <Zap className="h-3.5 w-3.5" />
                Automations
              </TabsTrigger>
            )}
            {isOwner && (
              <TabsTrigger
                value="role-permissions"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:text-foreground data-[state=active]:font-semibold px-4 py-3 text-[13px] font-medium text-muted-foreground gap-1.5 hover:text-foreground transition-colors duration-150"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                Permissions
              </TabsTrigger>
            )}
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            {tabs.columns && (
              <TabsContent
                value="columns"
                className="mt-0 p-5 data-[state=active]:animate-in data-[state=inactive]:animate-out data-[state=inactive]:fade-out-0 data-[state=active]:fade-in-0 duration-150"
              >
                <ColumnsTab projectId={projectId} />
              </TabsContent>
            )}
            {tabs.tags && (
              <TabsContent
                value="tags"
                className="mt-0 p-5 data-[state=active]:animate-in data-[state=inactive]:animate-out data-[state=inactive]:fade-out-0 data-[state=active]:fade-in-0 duration-150"
              >
                <TagsTab projectId={projectId} />
              </TabsContent>
            )}
            {tabs.members && (
              <TabsContent
                value="members"
                className="mt-0 p-5 data-[state=active]:animate-in data-[state=inactive]:animate-out data-[state=inactive]:fade-out-0 data-[state=active]:fade-in-0 duration-150"
              >
                <MembersTab projectId={projectId} />
              </TabsContent>
            )}
            {isOwner && (
              <TabsContent
                value="role-permissions"
                className="mt-0 p-5 data-[state=active]:animate-in data-[state=inactive]:animate-out data-[state=inactive]:fade-out-0 data-[state=active]:fade-in-0 duration-150"
              >
                <RolePermissionsTab projectId={projectId} />
              </TabsContent>
            )}
            {tabs.general && (
            <TabsContent
              value="general"
              className="mt-0 p-5 data-[state=active]:animate-in data-[state=inactive]:animate-out data-[state=inactive]:fade-out-0 data-[state=active]:fade-in-0 duration-150"
            >
              <GeneralTab
                projectId={projectId}
                onBoardDeleted={() => {
                  onOpenChange(false);
                  onBoardDeleted?.();
                }}
              />
            </TabsContent>
            )}
            {canManageAutomations && (
              <TabsContent
                value="automations"
                className="mt-0 p-5 data-[state=active]:animate-in data-[state=inactive]:animate-out data-[state=inactive]:fade-out-0 data-[state=active]:fade-in-0 duration-150"
              >
                <AutomationsTab boardId={projectId} />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sortable Column Row ──────────────────────────────────────────────────

interface SortableColumnRowProps {
  status: TaskStatusItem;
  isEditing: boolean;
  count: number;
  onStartEdit: (s: TaskStatusItem) => void;
  onDelete: (s: TaskStatusItem) => void;
  renderEditContent: React.ReactNode;
}

function SortableColumnRow({
  status,
  isEditing,
  count,
  onStartEdit,
  onDelete,
  renderEditContent,
}: SortableColumnRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: status.id,
    disabled: isEditing,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  if (isEditing) {
    return <div>{renderEditContent}</div>;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-muted/80 transition-colors"
    >
      <div
        {...attributes}
        {...listeners}
        className="w-4 shrink-0 flex items-center justify-center opacity-40 group-hover:opacity-100 cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <span
        className="h-3.5 w-3.5 rounded-full shrink-0 border-2 border-transparent transition-transform hover:scale-110"
        style={{ background: status.color }}
      />
      <span className="flex-1 text-[13px] font-medium">{status.name}</span>
      <span
        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
        style={{
          color: CATEGORIES.find((c) => c.value === status.category)?.color,
          background: `${CATEGORIES.find((c) => c.value === status.category)?.color}15`,
        }}
      >
        {status.category}
      </span>
      <span className="text-[11px] text-muted-foreground font-mono tabular-nums min-w-[28px] text-right">
        {count}
      </span>
      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          className="h-[26px] w-[26px] flex items-center justify-center rounded-full hover:bg-muted cursor-pointer text-muted-foreground hover:text-foreground"
          onClick={() => onStartEdit(status)}
          aria-label="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="h-[26px] w-[26px] flex items-center justify-center rounded-full hover:bg-muted cursor-pointer text-muted-foreground hover:text-destructive"
          onClick={() => onDelete(status)}
          aria-label="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Columns Tab ──────────────────────────────────────────────────────────

function ColumnsTab({ projectId }: { projectId: string }) {
  const { data: statuses = [] } = useStatuses(projectId);
  const { data: taskStats } = useProjectTaskStats(projectId);
  const countByStatusId = new Map(taskStats?.byStatus?.map((s) => [s.id, s.count]) ?? []);
  const createMutation = useCreateStatus(projectId);
  const updateMutation = useUpdateStatus();
  const deleteMutation = useDeleteStatus();
  const reorderMutation = useReorderStatuses(projectId);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', color: '', category: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', color: STATUS_COLORS[0], category: 'active' });
  const [deleteTarget, setDeleteTarget] = useState<TaskStatusItem | null>(null);
  const [migrateToId, setMigrateToId] = useState('');

  const sorted = [...statuses].sort((a, b) => a.sortOrder - b.sortOrder);

  const startEdit = (s: TaskStatusItem) => {
    setEditingId(s.id);
    setEditForm({ name: s.name, color: s.color, category: s.category });
  };

  const saveEdit = () => {
    if (!editingId || !editForm.name.trim()) return;
    updateMutation.mutate(
      {
        id: editingId,
        name: editForm.name.trim(),
        color: editForm.color,
        category: editForm.category,
      },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleAdd = () => {
    if (!addForm.name.trim()) return;
    createMutation.mutate(
      { name: addForm.name.trim(), color: addForm.color, category: addForm.category },
      {
        onSuccess: () => {
          setAddForm({ name: '', color: STATUS_COLORS[0], category: 'active' });
          setIsAdding(false);
        },
      }
    );
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { id: deleteTarget.id, migrateToStatusId: migrateToId || undefined },
      {
        onSuccess: () => {
          setDeleteTarget(null);
          setMigrateToId('');
        },
      }
    );
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = sorted.findIndex((s) => s.id === active.id);
      const newIndex = sorted.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(sorted, oldIndex, newIndex);
      const items = reordered.map((s, i) => ({ id: s.id, sortOrder: i }));
      reorderMutation.mutate(items);
    },
    [sorted, reorderMutation]
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">Board Columns</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Drag to reorder. Each column has a category that determines workflow behavior.
          </p>
        </div>
        <button
          type="button"
          className={cn(
            'flex h-9 w-9 items-center justify-center',
            'rounded-full bg-hygge/10',
            'transition-colors',
            'hover:bg-hygge/20',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'cursor-pointer',
            isAdding && 'opacity-50 pointer-events-none'
          )}
          onClick={() => setIsAdding(true)}
          disabled={isAdding}
          aria-label="Add column"
        >
          <Plus className="h-4 w-4 text-hygge" />
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sorted.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-0.5">
            {sorted.map((s) => (
              <div key={s.id}>
                <SortableColumnRow
                  status={s}
                  isEditing={editingId === s.id}
                  count={countByStatusId.get(s.id) ?? 0}
                  onStartEdit={startEdit}
                  onDelete={setDeleteTarget}
                  renderEditContent={
                    <div className="rounded-lg border border-hygge/20 bg-hygge/10 p-3">
                      <div className="flex items-center gap-3 mb-3">
                        <span
                          className="h-3.5 w-3.5 rounded-full shrink-0"
                          style={{ background: editForm.color }}
                        />
                        <Input
                          data-slot="board-column-edit-input"
                          value={editForm.name}
                          onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                          className="h-8 text-sm flex-1 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                        />
                      </div>

                      <div className="flex items-center gap-1.5 mb-3">
                        {STATUS_COLORS.map((c) => (
                          <button
                            key={c}
                            className={cn(
                              'h-5 w-5 rounded-full transition-transform cursor-pointer',
                              editForm.color === c &&
                                'ring-2 ring-offset-1 ring-foreground scale-110'
                            )}
                            style={{ background: c }}
                            onClick={() => setEditForm((f) => ({ ...f, color: c }))}
                          />
                        ))}
                      </div>

                      <div className="flex items-center gap-1.5 mb-3">
                        {CATEGORIES.map((cat) => {
                          const isActive = editForm.category === cat.value;
                          return (
                            <button
                              key={cat.value}
                              type="button"
                              className={cn(
                                'px-2.5 py-1 rounded-md text-[11px] font-medium cursor-pointer border transition-colors',
                                isActive
                                  ? 'border-transparent text-white'
                                  : 'border-border hover:opacity-90'
                              )}
                              style={{
                                background: isActive ? cat.color : `${cat.color}15`,
                                color: isActive ? '#fff' : cat.color,
                              }}
                              onClick={() => setEditForm((f) => ({ ...f, category: cat.value }))}
                            >
                              {cat.label}
                            </button>
                          );
                        })}
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs cursor-pointer border border-border"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 text-xs font-semibold cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90 border-0"
                          onClick={saveEdit}
                          disabled={updateMutation.isPending}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  }
                />
              </div>
            ))}

            {isAdding && (
              <div className="mt-1 p-3 rounded-md border border-dashed border-border hover:border-hygge/30 hover:bg-hygge/5 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="h-3.5 w-3.5 rounded-full shrink-0"
                    style={{ background: addForm.color }}
                  />
                  <Input
                    data-slot="board-column-edit-input"
                    value={addForm.name}
                    onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Column name..."
                    className="h-8 text-sm flex-1 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAdd();
                      if (e.key === 'Escape') setIsAdding(false);
                    }}
                  />
                </div>

                <div className="flex items-center gap-1.5 mb-3">
                  {STATUS_COLORS.map((c) => (
                    <button
                      key={c}
                      className={cn(
                        'h-5 w-5 rounded-full transition-transform cursor-pointer',
                        addForm.color === c && 'ring-2 ring-offset-1 ring-foreground scale-110'
                      )}
                      style={{ background: c }}
                      onClick={() => setAddForm((f) => ({ ...f, color: c }))}
                    />
                  ))}
                </div>

                <div className="flex items-center gap-1.5 mb-3">
                  {CATEGORIES.map((cat) => {
                    const isActive = addForm.category === cat.value;
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        className={cn(
                          'px-2.5 py-1 rounded-md text-[11px] font-medium cursor-pointer border transition-colors',
                          isActive
                            ? 'border-transparent text-white'
                            : 'border-border hover:opacity-90'
                        )}
                        style={{
                          background: isActive ? cat.color : `${cat.color}15`,
                          color: isActive ? '#fff' : cat.color,
                        }}
                        onClick={() => setAddForm((f) => ({ ...f, category: cat.value }))}
                      >
                        {cat.label}
                      </button>
                    );
                  })}
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs cursor-pointer border border-border"
                    onClick={() => setIsAdding(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs font-semibold cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90 border-0"
                    onClick={handleAdd}
                    disabled={createMutation.isPending || !addForm.name.trim()}
                  >
                    Add Column
                  </Button>
                </div>
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => {
          if (!v) {
            setDeleteTarget(null);
            setMigrateToId('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete column "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this column. If tasks exist in this column, choose where
              to move them.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteTarget && (
            <div className="py-2">
              <label className="text-sm font-medium mb-1.5 block">Move tasks to:</label>
              <Select value={migrateToId} onValueChange={setMigrateToId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select column (or leave empty if no tasks)" />
                </SelectTrigger>
                <SelectContent>
                  {sorted
                    .filter((s) => s.id !== deleteTarget.id)
                    .map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ background: s.color }}
                          />
                          {s.name}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 cursor-pointer"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Tags Tab ─────────────────────────────────────────────────────────────

function TagsTab({ projectId }: { projectId: string }) {
  const { data: tags = [] } = useProjectTags(projectId);
  const createMutation = useCreateTag(projectId);
  const updateMutation = useUpdateTag(projectId);
  const deleteMutation = useDeleteTag(projectId);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', color: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', color: STATUS_COLORS[0] });

  const startEdit = (t: TaskTagItem) => {
    setEditingId(t.id);
    setEditForm({ name: t.name, color: t.color });
  };

  const saveEdit = () => {
    if (!editingId || !editForm.name.trim()) return;
    updateMutation.mutate(
      { id: editingId, name: editForm.name.trim(), color: editForm.color },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleAdd = () => {
    if (!addForm.name.trim()) return;
    createMutation.mutate(
      { name: addForm.name.trim(), color: addForm.color },
      {
        onSuccess: () => {
          setAddForm({ name: '', color: STATUS_COLORS[0] });
          setIsAdding(false);
        },
      }
    );
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">Project Tags</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Tags help categorize tasks. Click a color to change it.
          </p>
        </div>
        <button
          type="button"
          className={cn(
            'flex h-9 w-9 items-center justify-center',
            'rounded-full bg-hygge/10',
            'transition-colors',
            'hover:bg-hygge/20',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'cursor-pointer',
            isAdding && 'opacity-50 pointer-events-none'
          )}
          onClick={() => setIsAdding(true)}
          disabled={isAdding}
          aria-label="Add tag"
        >
          <Plus className="h-4 w-4 text-hygge" />
        </button>
      </div>

      <div className="space-y-0.5">
        {tags.map((t) => (
          <div key={t.id}>
            {editingId === t.id ? (
              <div className="p-3 rounded-lg border border-hygge/20 bg-hygge/10">
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="h-3.5 w-3.5 rounded-full shrink-0"
                    style={{ background: editForm.color }}
                  />
                  <Input
                    data-slot="board-tag-edit-input"
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    className="h-8 text-sm flex-1 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                  />
                </div>

                <div className="flex items-center gap-1.5 mb-3">
                  {STATUS_COLORS.map((c) => (
                    <button
                      key={c}
                      className={cn(
                        'h-5 w-5 rounded-full transition-transform cursor-pointer',
                        editForm.color === c && 'ring-2 ring-offset-1 ring-foreground scale-110'
                      )}
                      style={{ background: c }}
                      onClick={() => setEditForm((f) => ({ ...f, color: c }))}
                    />
                  ))}
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs cursor-pointer border border-border"
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs font-semibold cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90 border-0"
                    onClick={saveEdit}
                    disabled={updateMutation.isPending}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="group flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-muted/80 transition-colors">
                <span className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[10px] font-mono font-medium text-muted-foreground bg-muted/50">
                  <span
                    className="h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: t.color }}
                  />
                  {t.name}
                </span>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-auto">
                  <button
                    className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-muted cursor-pointer"
                    onClick={() => startEdit(t)}
                  >
                    <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                  <button
                    className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-muted cursor-pointer"
                    onClick={() => handleDelete(t.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {isAdding && (
          <div className="mt-1 p-3 rounded-md border border-dashed border-border hover:border-hygge/30 hover:bg-hygge/5 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <span
                className="h-3.5 w-3.5 rounded-full shrink-0"
                style={{ background: addForm.color }}
              />
              <Input
                data-slot="board-tag-edit-input"
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Tag name..."
                className="h-8 text-sm flex-1 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd();
                  if (e.key === 'Escape') setIsAdding(false);
                }}
              />
            </div>

            <div className="flex items-center gap-1.5 mb-3">
              {STATUS_COLORS.map((c) => (
                <button
                  key={c}
                  className={cn(
                    'h-5 w-5 rounded-full transition-transform cursor-pointer',
                    addForm.color === c && 'ring-2 ring-offset-1 ring-foreground scale-110'
                  )}
                  style={{ background: c }}
                  onClick={() => setAddForm((f) => ({ ...f, color: c }))}
                />
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs cursor-pointer border border-border"
                onClick={() => setIsAdding(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs font-semibold cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90 border-0"
                onClick={handleAdd}
                disabled={createMutation.isPending || !addForm.name.trim()}
              >
                Add Tag
              </Button>
            </div>
          </div>
        )}

        {tags.length === 0 && !isAdding && (
          <div className="text-center py-8">
            <Tag className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">
              No tags yet. Tags will be auto-created when first accessed, or add one manually.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Members Tab ──────────────────────────────────────────────────────────

function MembersTab({ projectId }: { projectId: string }) {
  const formatName = useEmployeeNameFormatter();
  const { data: members = [] } = useBoardMembers(projectId);
  const addMembersMutation = useAddBoardMembers(projectId);
  const removeMemberMutation = useRemoveBoardMember(projectId);
  // The server caps a grant at the caller’s own rank and refuses changing
  // anybody above them, so neither is offered here.
  const grantable = useGrantableBoardRoles(projectId);
  const mayChange = useMayChangeBoardMember(projectId);
  // A board left with no owner cannot be administered by anyone, so the last
  // one is not offered a way down. The server refuses it too.
  // Counted the way the server counts owners: a board creator listed only
  // through the project holds the owner tier without a row of their own.
  const ownerCount = members.filter((m) => m.effectiveRole === 'owner').length;
  // Radix takes the trigger label from the SELECTED item, so a role that is
  // not offered has to be listed all the same — disabled — or the box shows
  // empty. That also covers the first paint, before the board has loaded and
  // the caller has no grantable set yet.
  const rolesFor = (current: string): BoardMemberRole[] =>
    BOARD_ROLE_ORDER.filter((r) => r === current || grantable.includes(r));
  const changeRoleMutation = useChangeBoardMemberRole(projectId);

  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingMember, setIsAddingMember] = useState(false);

  const handleAddMember = async (employeeId: string) => {
    await addMembersMutation.mutateAsync({ employeeIds: [employeeId] });
    setSearchQuery('');
    setIsAddingMember(false);
  };

  const handleRemoveMember = (employeeId: string) => {
    // The board can refuse this — the last owner, or somebody who outranks the
    // caller. Without this the row simply did not change and nothing said why.
    removeMemberMutation.mutate(employeeId, {
      onError: (e) => toast.error(extractErrorMessage(e, 'Could not remove that member')),
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">Board Members</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            People who can be assigned to tasks on this board.
          </p>
        </div>
        <button
          type="button"
          className={cn(
            'flex h-9 w-9 items-center justify-center',
            'rounded-full bg-hygge/10',
            'transition-colors',
            'hover:bg-hygge/20',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'cursor-pointer',
            isAddingMember && 'opacity-50 pointer-events-none'
          )}
          onClick={() => setIsAddingMember(true)}
          disabled={isAddingMember || !grantable.includes('member')}
          aria-label="Add member"
        >
          <Plus className="h-4 w-4 text-hygge" />
        </button>
      </div>


      {isAddingMember && (
        <div className="mb-4 p-3 rounded-md border border-dashed border-border hover:border-hygge/30 hover:bg-hygge/5 transition-colors">
          <AddMemberSearch
            existingMemberIds={members.map((m) => m.employeeId)}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSelect={handleAddMember}
            onCancel={() => {
              setIsAddingMember(false);
              setSearchQuery('');
            }}
          />
        </div>
      )}

      <div className="space-y-0.5">
        {members.map((m) => (
          <div
            key={m.employeeId}
            className="group flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarImage src={m.avatarUrl || undefined} />
              <AvatarFallback className="text-[10px] font-semibold bg-(--hygge) text-white">
                {(m.firstName || '')[0]}
                {(m.lastName || '')[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {formatName({ firstName: m.firstName, lastName: m.lastName })}
              </div>
              {/* No fallback text: a placeholder here read as the board role
                  beside the role picker. */}
              {(m.departmentName || m.role === null) && (
                <div className="text-xs text-muted-foreground truncate">
                  {[m.departmentName, m.role === null && (m.viaProject ? 'via project' : m.source)]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
            </div>
            {m.effectiveRole ? (
              <Select
                value={m.effectiveRole}
                disabled={!mayChange(m.effectiveRole)}
                onValueChange={(role: string) => {
                  const onError = (e: unknown) =>
                    toast.error(extractErrorMessage(e, 'Could not change that role'));
                  // Somebody here without a board row — through the project, or
                  // as its creator — has no row to change; giving them one of
                  // their own is how their role on this board is set, and it
                  // outranks the tier they hold now.
                  if (m.role === null) {
                    addMembersMutation.mutate(
                      { employeeIds: [m.employeeId], role: role as BoardMemberRole },
                      { onError },
                    );
                  } else {
                    changeRoleMutation.mutate({ employeeId: m.employeeId, role }, { onError });
                  }
                }}
              >
                <SelectTrigger className="w-24 h-7 text-[11px] border-transparent hover:border-border transition-colors">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rolesFor(m.effectiveRole).map((role) => (
                    <SelectItem
                      key={role}
                      value={role}
                      disabled={
                        !grantable.includes(role) ||
                        (m.effectiveRole === 'owner' && role !== 'owner' && ownerCount === 1)
                      }
                    >
                      <span className="text-xs capitalize">{role}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {/* Only a board row can be removed; somebody here through the
                project leaves with the project, and both member routes answer
                404 for them. The last owner is not offered it either — the
                server refuses a board with nobody left at the top. */}
            {m.role &&
              mayChange(m.role) &&
              !(m.effectiveRole === 'owner' && ownerCount === 1) && (
              <button
                type="button"
                className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-muted cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                onClick={() => handleRemoveMember(m.employeeId)}
                aria-label="Remove member"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {members.length === 0 && !isAddingMember && (
        <div className="text-center py-8">
          <Users className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">
            No members yet. Add people to control who can be assigned to tasks.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Add Member Search ──────────────────────────────────────────────────
import { useEmployees } from '@/hooks/use-employees';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/lib/errors';
import { BOARD_ROLE_ORDER } from '@hygge/shared';
import type { BoardMemberRole } from '@hygge/shared';

function AddMemberSearch({
  existingMemberIds,
  searchQuery,
  onSearchChange,
  onSelect,
  onCancel,
}: {
  existingMemberIds: string[];
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onSelect: (employeeId: string) => void;
  onCancel: () => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const { data: employeesData } = useEmployees({ search: searchQuery, limit: 10, status: 'active' });
  const employees = employeesData?.data ?? [];
  const filtered = employees.filter((e) => !existingMemberIds.includes(e.id));

  return (
    <div>
      <Input
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search employees by name..."
        className="h-8 text-sm mb-2"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
      />
      {searchQuery.length > 0 && (
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {filtered.map((e) => (
            <button
              key={e.id}
              type="button"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/80 transition-colors cursor-pointer text-left"
              onClick={() => onSelect(e.id)}
            >
              <Avatar className="h-6 w-6 shrink-0">
                <AvatarImage src={e.avatarUrl || undefined} />
                <AvatarFallback className="text-[8px]">
                  {e.firstName[0]}
                  {e.lastName[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium truncate">
                  {formatName({ firstName: e.firstName, lastName: e.lastName })}
                </span>
              </div>
            </button>
          ))}
          {filtered.length === 0 && employees.length > 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">
              All matching employees are already members
            </p>
          )}
          {employees.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">No employees found</p>
          )}
        </div>
      )}
      <div className="flex justify-end mt-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs cursor-pointer border border-border"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ─── General Tab ──────────────────────────────────────────────────────────

function GeneralTab({
  projectId,
  onBoardDeleted,
}: {
  projectId: string;
  onBoardDeleted?: () => void;
}) {
  const { data: statuses = [] } = useStatuses(projectId);
  const { data: settings } = useBoardSettings(projectId);
  const updateSettings = useUpdateBoardSettings(projectId);
  const deleteBoardMutation = useDeleteBoard();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleChange = (key: string, value: unknown) => {
    updateSettings.mutate({ [key]: value });
  };

  const handleDeleteBoard = async () => {
    await deleteBoardMutation.mutateAsync(projectId);
    // Force clear all board-related caches before navigating
    await queryClient.invalidateQueries({ queryKey: boardKeys.lists() });
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['task-statuses'] });
    await queryClient.invalidateQueries({ queryKey: ['sprints'] });
    setShowDeleteConfirm(false);
    onBoardDeleted?.();
  };

  const { data: boardData } = useBoard(projectId);
  const updateBoard = useUpdateBoard();
  // Two controls on this tab cost more than the tab does: changing who can
  // see the board, and deleting it. A board Admin holds `board.settings` and
  // may hold neither of these, so each is asked for separately.
  const canChangeVisibility = useBoardPermission(projectId, 'visibility.manage');
  const canDeleteBoard = useBoardPermission(projectId, 'board.delete');
  const canEditSettings = useBoardPermission(projectId, 'board.settings');
  // Linking the board to a project admits nobody (HW-983): a project picked
  // here waits while its people are ticked, and both are saved together.
  // Ticking needs `member.manage`; without it the board is linked alone.
  const canManageMembers = useBoardPermission(projectId, 'member.manage');
  const { data: boardMembers = [] } = useBoardMembers(projectId);
  const onBoard = useMemo(() => new Set(boardMembers.map((m) => m.employeeId)), [boardMembers]);
  const [linking, setLinking] = useState<ProjectOption | null>(null);
  const [linkPeople, setLinkPeople] = useState<string[]>([]);
  // Adding people of the project the board already has: the same picker,
  // saved with `projectMemberIds` alone, which costs `member.manage` only.
  const [addingPeople, setAddingPeople] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  // The confirmation is opened from the picker's ✕, not from a dialog trigger,
  // so Radix has nothing to give focus back to: it goes back to the ✕ here.
  const unlinkButtonRef = useRef<HTMLButtonElement>(null);
  // After a confirmed unlink the ✕ goes with the link: focus goes to the
  // picker itself instead.
  const projectTriggerRef = useRef<HTMLButtonElement>(null);
  const unlinkConfirmed = useRef(false);
  const pickerHintId = useId();
  const linkedProjectId = boardData?.projectId ?? null;
  const chooseProject = (project: ProjectOption | null) => {
    setLinkPeople([]);
    setAddingPeople(false);
    if (!project) {
      // ✕ on a pick not saved yet only drops the pick; on the saved project
      // it unlinks the board, and asks first.
      const pending = linking !== null;
      setLinking(null);
      if (!pending && linkedProjectId) {
        unlinkConfirmed.current = false;
        setConfirmUnlink(true);
      }
    } else if (project.id.toLowerCase() === linkedProjectId?.toLowerCase()) {
      // Back to the project it already has: nothing is pending.
      setLinking(null);
    } else {
      setLinking(project);
    }
  };
  // The dialog stays open while the unlink is saved, and closes only once it
  // is done; a failure leaves it open, and the app's error toast says why.
  const unlink = (event: React.MouseEvent) => {
    event.preventDefault();
    updateBoard.mutate(
      { id: projectId, projectId: null },
      {
        onSuccess: () => {
          unlinkConfirmed.current = true;
          setConfirmUnlink(false);
        },
      },
    );
  };
  const saveAddedPeople = () => {
    if (linkPeople.length === 0) return;
    updateBoard.mutate(
      { id: projectId, projectMemberIds: linkPeople },
      {
        onSuccess: () => {
          setAddingPeople(false);
          setLinkPeople([]);
        },
      },
    );
  };
  const saveLink = () => {
    if (!linking) return;
    updateBoard.mutate(
      {
        id: projectId,
        projectId: linking.id,
        ...(canManageMembers && linkPeople.length > 0 && { projectMemberIds: linkPeople }),
      },
      { onSuccess: () => setLinking(null) },
    );
  };

  return (
    <div className="space-y-7">
      {(canEditSettings || (canManageMembers && linkedProjectId)) && (
      <section>
        <div className="flex items-center gap-2 mb-1">
          <FolderKanban className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-[13px] font-semibold text-foreground">Project</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          The project this board is listed under. Linking a board gives nobody access to it: its members are its own.
        </p>
        <div className="w-72 max-w-full">
          <ProjectCombobox
            value={
              linking ??
              (boardData?.projectId
                ? { id: boardData.projectId, name: boardData.projectName ?? 'Unnamed project' }
                : null)
            }
            onChange={chooseProject}
            clearRef={unlinkButtonRef}
            triggerRef={projectTriggerRef}
            disabled={!canEditSettings || updateBoard.isPending}
          />
        </div>
        {linkedProjectId && !linking && (
          <div className="mt-3 max-w-md space-y-2">
            {!canManageMembers ? (
              <p className="text-[11px] text-muted-foreground">
                Your role on this board cannot add members, so the project's people cannot be added from here.
              </p>
            ) : !addingPeople ? (
              <Button size="sm" variant="outline" className="text-xs" onClick={() => setAddingPeople(true)}>
                <Users className="h-3.5 w-3.5 mr-1" />
                Add project members
              </Button>
            ) : (
              <>
                <p id={pickerHintId} className="text-[11px] text-muted-foreground">
                  Tick the project's people who should join this board as Members.
                </p>
                <ProjectMembersPicker
                  projectId={linkedProjectId}
                  labelledBy={pickerHintId}
                  onBoard={onBoard}
                  value={linkPeople}
                  onChange={setLinkPeople}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="text-xs"
                    onClick={saveAddedPeople}
                    disabled={linkPeople.length === 0 || updateBoard.isPending}
                  >
                    {linkPeople.length > 0
                      ? `Add ${linkPeople.length} ${linkPeople.length === 1 ? 'member' : 'members'}`
                      : 'Add members'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() => {
                      setAddingPeople(false);
                      setLinkPeople([]);
                    }}
                    disabled={updateBoard.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        {linking && (
          <div className="mt-3 max-w-md space-y-2">
            {canManageMembers ? (
              <>
                <p id={pickerHintId} className="text-[11px] text-muted-foreground">
                  Tick the project's people who should join this board as Members.
                </p>
                <ProjectMembersPicker
                  projectId={linking.id}
                  labelledBy={pickerHintId}
                  onBoard={onBoard}
                  value={linkPeople}
                  onChange={setLinkPeople}
                />
              </>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Your role on this board cannot add members, so the board is linked without adding any.
              </p>
            )}
            <div className="flex gap-2">
              <Button size="sm" className="text-xs" onClick={saveLink} disabled={updateBoard.isPending}>
                {linkPeople.length > 0 && canManageMembers
                  ? `Link and add ${linkPeople.length} ${linkPeople.length === 1 ? 'member' : 'members'}`
                  : 'Link board'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs"
                onClick={() => {
                  setLinking(null);
                  setLinkPeople([]);
                }}
                disabled={updateBoard.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        <AlertDialog
          open={confirmUnlink}
          onOpenChange={(open) => {
            // Not while the unlink is being saved: its result closes the dialog.
            if (!open && !updateBoard.isPending) setConfirmUnlink(false);
          }}
        >
          <AlertDialogContent
            onCloseAutoFocus={(event) => {
              // Cancel or Escape: back to the ✕. Unlinked: the ✕ is going with
              // the link, so to the picker.
              const target = unlinkConfirmed.current ? projectTriggerRef.current : unlinkButtonRef.current;
              unlinkConfirmed.current = false;
              if (!target) return;
              event.preventDefault();
              // Next frame: the picker is disabled while the save settles.
              requestAnimationFrame(() => target.focus());
            }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Unlink this board from {boardData?.projectName ?? 'its project'}?</AlertDialogTitle>
              <AlertDialogDescription>
                The board leaves the project and stays as it is otherwise: its tasks, sprints and columns are kept,
                and its members keep their access. Its activity no longer shows on the project's page.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="cursor-pointer" disabled={updateBoard.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction className="cursor-pointer" onClick={unlink} disabled={updateBoard.isPending}>
                {updateBoard.isPending ? 'Unlinking...' : 'Unlink'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>
      )}

      {/* Access Control */}
      {canChangeVisibility && (
      <section>
        <div className="flex items-center gap-2 mb-1">
          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-[13px] font-semibold text-foreground">Access</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Control who can see and access this board.
        </p>

        <div className="divide-y divide-border">
          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">Visibility</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {boardData?.visibility === 'members_only'
                  ? 'Only members and admins can see this board'
                  : 'Everyone with access to Boards can read this board; editing still needs a board role'}
              </div>
            </div>
            <Select
              value={boardData?.visibility ?? 'public'}
              onValueChange={(v) =>
                updateBoard.mutate({ id: projectId, visibility: v as 'public' | 'members_only' })
              }
            >
              <SelectTrigger className="w-44 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="members_only">Private</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>
      )}

      {canEditSettings && (
      <>
      {/* Defaults */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <Sliders className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-[13px] font-semibold text-foreground">Defaults</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Configure what happens when new tasks are created.
        </p>

        <div className="divide-y divide-border">
          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">Default status</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                New tasks will be created in this column
              </div>
            </div>
            <Select
              value={settings?.defaultStatusId || ''}
              onValueChange={(v) => handleChange('defaultStatusId', v || null)}
            >
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue placeholder="Auto (Backlog)" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                      {s.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">Default assignee</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Who gets auto-assigned when creating a task
              </div>
            </div>
            <Select
              value={settings?.defaultAssignee || 'none'}
              onValueChange={(v) => handleChange('defaultAssignee', v)}
            >
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No one</SelectItem>
                <SelectItem value="creator">Creator</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Lives here, not on Members: it is a board setting, priced at
              `board.settings`, and Members opens on `member.manage` — so a role
              with the first but not the second could set it on the API and
              never reach the switch. */}
          <div className="flex items-center justify-between py-3 gap-4">
            <div>
              <div className="text-sm font-medium">Allow assigning anyone in the organization</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                When enabled, all employees appear in the assignee search — not just board members.
              </div>
            </div>
            <Switch
              checked={settings?.allowOrgWideAssignees ?? false}
              onCheckedChange={(v) => handleChange('allowOrgWideAssignees', v)}
              className="cursor-pointer"
            />
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">Estimation unit</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                How effort is measured on tasks
              </div>
            </div>
            <Select
              value={settings?.estimationUnit || 'story_points'}
              onValueChange={(v) => handleChange('estimationUnit', v)}
            >
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="story_points">Story Points</SelectItem>
                <SelectItem value="hours">Hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Automation */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <svg
            className="h-3.5 w-3.5 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          <h3 className="text-sm font-semibold">Automation</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Rules that run automatically on your board.
        </p>

        <div className="divide-y divide-border">
          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">Auto-archive done tasks</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Hide completed tasks after a period
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={(settings?.autoArchiveDays ?? 0) > 0}
                onCheckedChange={(checked) => handleChange('autoArchiveDays', checked ? 30 : 0)}
                className="cursor-pointer"
              />
              {(settings?.autoArchiveDays ?? 0) > 0 && (
                <>
                  <Input
                    type="number"
                    value={settings?.autoArchiveDays ?? 30}
                    onChange={(e) => handleChange('autoArchiveDays', parseInt(e.target.value) || 0)}
                    className="w-14 h-7 text-xs text-center"
                    min={1}
                    max={365}
                  />
                  <span className="text-xs text-muted-foreground">days</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-medium">Show completed tasks on board</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                If off, "Done" column is hidden from board view
              </div>
            </div>
            <Switch
              checked={settings?.showDoneColumn ?? true}
              onCheckedChange={(v) => handleChange('showDoneColumn', v)}
              className="cursor-pointer"
            />
          </div>
        </div>
      </section>
      </>
      )}

      {/* Danger Zone */}
      {canDeleteBoard && (
      <section>
        <div className="flex items-center gap-2 mb-1">
          <svg
            className="h-3.5 w-3.5 text-destructive"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="text-sm font-semibold text-destructive">Danger Zone</h3>
        </div>

        <div className="space-y-3">
          <div className="p-3.5 rounded-lg border border-destructive/20 bg-destructive/5 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-destructive">Delete board</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Permanently delete this board, all tasks, sprints, and statuses
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive border-destructive/30 hover:bg-destructive hover:text-destructive-foreground cursor-pointer text-xs"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      </section>
      )}

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this board?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the board and all its data including tasks, sprints,
              statuses, and tags. This action cannot be undone.
              <br />
              <br />
              You will be redirected to the default board.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 cursor-pointer"
              onClick={handleDeleteBoard}
              disabled={deleteBoardMutation.isPending}
            >
              {deleteBoardMutation.isPending ? 'Deleting...' : 'Delete Board'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
